import { Injectable } from "@angular/core";
import { FormFieldChange } from "src/app/items/components/item-form/dynamic-form-actions-base";
import { User } from "src/app/session/models/user";
import { HttpService } from "src/app/session/services/http.service";
import { environment } from "src/environments/environment";
import { QueryHandler } from "../../shared/ddd/contracts/query-handler.interface";
import { CommandHandler } from "../../shared/ddd/contracts/command-handler.interface";
import { InvokeResult } from "src/app/shared/ddd/models/invoke-result";
import { Query } from "src/app/shared/ddd/models/query";
import { Command } from "src/app/shared/ddd/models/command";
import { ScopeOfWorkQuery } from "./queries/scope-of-work.query";
import { ItFieldInfo } from "../../inspection/model/it-field-info";
import { Observable, Subject } from "rxjs";
import { ScopeOfWorkFieldChangeCommand } from "./commands/scope-of-work-field-change.command";
import { DynamicFormData } from "src/app/inspection/model/dynamic-form-data";
import { CotecnaUserService } from "@cotecna/session";

@Injectable({
  providedIn: "root",
})
export class ScopeOfWorkService implements QueryHandler, CommandHandler {
  private _scopeOfWorkFormData = new Subject<DynamicFormData>();

  public get scopeOfWorkFormData(): Observable<DynamicFormData> {
    return this._scopeOfWorkFormData;
  }

  constructor(
    private httpService: HttpService,
    private userService: CotecnaUserService,
  ) {}

  public invokeQuery<TQuery extends Query>(
    query: TQuery,
  ): Promise<InvokeResult> {
    switch (query.constructor) {
      case ScopeOfWorkQuery:
        return this.invokeScopeOfWork(query);
      default:
        throw new Error("Query not allowed");
    }
  }

  public invokeCommand<TCommand extends Command>(
    command: TCommand,
  ): Promise<InvokeResult> {
    switch (command.constructor) {
      case ScopeOfWorkFieldChangeCommand:
        return this.invokeScopeOfWorkFieldChange(command);
      default:
        throw new Error("Command not allowed");
    }
  }

  public async applyScopeOfWorkFieldChange(
    formValues: ItFieldInfo[],
    formFieldChange: FormFieldChange,
  ) {
    const fieldInfo = formValues.find(
      (fieldValue) => fieldValue.fieldId === formFieldChange.fieldId,
    );
    if (fieldInfo) {
      fieldInfo.value = formFieldChange.value;
      fieldInfo.modificationDate = formFieldChange.timestamp;
    } else {
      formValues.push({
        fieldId: formFieldChange.fieldId,
        value: formFieldChange.value,
        modificationDate: formFieldChange.timestamp,
        modifiedBy: this.userService.getUser().name,
      });
    }
  }

  private async invokeScopeOfWork<TQuery extends Query>(
    query: TQuery,
  ): Promise<InvokeResult> {
    const scopeOfWorkFormData: DynamicFormData =
      await this.getScopeOfWorkFormData(query);
    this._scopeOfWorkFormData.next(scopeOfWorkFormData);
    return { result: true, message: "get sow form data - OK" } as InvokeResult;
  }

  private getScopeOfWorkFormData(query: any): Promise<DynamicFormData> {
    const url = `${environment.webApiUrl}/api/inspectionDetails/scopeOfWork/${query.inspectionId}`;
    return this.httpService.get(url).toPromise();
  }

  private async invokeScopeOfWorkFieldChange<TCommand extends Command>(
    command: TCommand,
  ): Promise<InvokeResult> {
    await this.updateScopeOfWorkFieldChangeToNetwork(command);
    return {
      result: true,
      message: "update sow field change - OK",
    } as InvokeResult;
  }

  private async updateScopeOfWorkFieldChangeToNetwork(
    command: any,
  ): Promise<boolean> {
    return this.httpService
      .put(
        `${environment.webApiUrl}/api/inspection/updateInspectionFormWeb`,
        { fieldChanges: [command.fieldChange] },
        { withCredentials: true },
      )
      .toPromise();
  }
}
